import {
  AudioFrameSource,
  FrameProcessor,
  loadModel,
  Message,
  Model,
} from "@ricky0123/vad-web"
import { MAX_SESSION_MS, MODEL, VadParams } from "../shared/protocol"

export interface ClipEvent {
  /** Index into the params array given to setArms. */
  armIndex: number
  audio: Float32Array
  startMs: number
  endMs: number
}

/**
 * One model and one microphone, shared by every arm. Each frame is run
 * through the model once and the probability is handed to every arm's
 * FrameProcessor, so the arms segment the identical probability stream and
 * any difference in their clips is caused by the parameters alone. The model
 * is reset only between recordings, never during one, which keeps the trace
 * a pure function of the audio and is what makes replaying other parameter
 * settings over it afterwards meaningful.
 *
 * Every frame is also kept, because the annotation step needs the whole
 * session playable and scrubbable rather than just the clips the arms
 * happened to emit. That is what bounds a session to MAX_SESSION_MS: ten
 * minutes of 16 kHz mono is about 38 MB held in memory, and the recording
 * stops itself there rather than growing without limit.
 */
export class ComparisonDriver {
  trace: number[] = []
  onFrame: ((prob: number) => void) | null = null
  onClip: ((clip: ClipEvent) => void) | null = null
  /** Fires when the recording stops itself at MAX_SESSION_MS. */
  onLimit: (() => void) | null = null

  private arms: FrameProcessor[] = []
  private frameIndex = 0
  private frames: Float32Array[] = []
  private maxFrames: number
  private stopping = false

  private constructor(
    private model: Model,
    private source: AudioFrameSource,
    readonly frameSamples: number,
    readonly msPerFrame: number
  ) {
    this.maxFrames = Math.floor(MAX_SESSION_MS / msPerFrame)
    source.onFrame(this.processFrame)
  }

  static async create(): Promise<ComparisonDriver> {
    const { model, frameSamples, msPerFrame } = await loadModel({
      model: MODEL,
      baseAssetPath: "/",
      onnxWASMBasePath: "/",
    })
    const source = await AudioFrameSource.new({
      frameSamples,
      baseAssetPath: "/",
    })
    return new ComparisonDriver(model, source, frameSamples, msPerFrame)
  }

  get listening(): boolean {
    return this.source.listening
  }

  get recordingDurationMs(): number {
    return this.frameIndex * this.msPerFrame
  }

  /** The frame count a file has to fit under to be accepted. */
  get maxSessionFrames(): number {
    return this.maxFrames
  }

  /** Replaces the arms for a new session and clears all recording state. */
  setArms(paramSets: VadParams[]) {
    this.arms = paramSets.map(
      (params) =>
        new FrameProcessor(
          { ...params, submitUserSpeechOnPause: true },
          this.msPerFrame
        )
    )
    for (const fp of this.arms) fp.resume()
    this.trace = []
    this.frames = []
    this.frameIndex = 0
    this.stopping = false
    this.model.reset_state()
  }

  async start() {
    await this.source.start()
  }

  /** Stops the microphone and flushes each arm's held audio as a final clip. */
  async stop() {
    // Set before awaiting, so a frame already suspended inside the model
    // when Stop was pressed is discarded rather than landing in the trace
    // after the arms have been flushed. Without this the trace could end up
    // one frame longer than what the arms actually saw, which left the final
    // clip's boundary 32ms short of the recording length and made replay
    // disagree with the live output on exactly that segment.
    this.stopping = true
    await this.source.pause()
    this.flushArms()
    this.model.reset_state()
  }

  /**
   * Everything captured this session as one contiguous 16 kHz mono buffer.
   * The annotation timeline plays and scrubs over this; it is never
   * uploaded.
   */
  sessionAudio(): Float32Array {
    const out = new Float32Array(this.frames.length * this.frameSamples)
    this.frames.forEach((frame, i) => out.set(frame, i * this.frameSamples))
    return out
  }

  /**
   * Runs a decoded 16 kHz buffer through the same path a live recording
   * takes, yielding to the event loop periodically so the page stays
   * responsive. setArms must have been called first; call it again before
   * reusing the driver.
   */
  async processFile(
    audio: Float32Array,
    onProgress?: (fraction: number) => void
  ) {
    const totalFrames = Math.floor(audio.length / this.frameSamples)
    let done = 0
    for (
      let i = 0;
      i + this.frameSamples <= audio.length && this.frameIndex < this.maxFrames;
      i += this.frameSamples
    ) {
      await this.processFrame(audio.slice(i, i + this.frameSamples))
      if (++done % 50 === 0) {
        onProgress?.(done / totalFrames)
        await new Promise((r) => setTimeout(r, 0))
      }
    }
    this.flushArms()
    this.model.reset_state()
  }

  /** Emits whatever audio each arm is still holding as a final clip. */
  private flushArms() {
    const stopFrame = this.frameIndex
    this.arms.forEach((fp, armIndex) => {
      fp.pause((ev) => {
        if (ev.msg === Message.SpeechEnd) {
          const frameCount = ev.audio.length / this.frameSamples
          this.onClip?.({
            armIndex,
            audio: ev.audio,
            startMs: (stopFrame - frameCount) * this.msPerFrame,
            endMs: stopFrame * this.msPerFrame,
          })
        }
      })
    })
  }

  private processFrame = async (frame: Float32Array) => {
    if (this.frameIndex >= this.maxFrames || this.stopping) return

    const probs = await this.model.process(frame)
    // Checked again after the await: Stop may have been pressed while this
    // frame was inside the model, and the arms have already been flushed by
    // then, so recording it would describe audio they never segmented.
    if (this.stopping) return
    this.trace.push(Number(probs.isSpeech.toFixed(4)))
    this.frames.push(frame)
    const frameIndex = this.frameIndex

    this.arms.forEach((fp, armIndex) => {
      fp.process(frame, probs, (ev) => {
        if (ev.msg === Message.SpeechEnd) {
          // The emitted audio spans a whole number of frames ending at the
          // current one, so its extent follows from its length. These bounds
          // include preSpeechPadMs, which is deliberate: the padded span is
          // what the caller actually receives, and so is what model_spec.md
          // counts as emitted.
          const frameCount = ev.audio.length / this.frameSamples
          this.onClip?.({
            armIndex,
            audio: ev.audio,
            startMs: (frameIndex - frameCount + 1) * this.msPerFrame,
            endMs: (frameIndex + 1) * this.msPerFrame,
          })
        }
      })
    })

    this.frameIndex++
    this.onFrame?.(probs.isSpeech)

    if (this.frameIndex >= this.maxFrames && this.source.listening) {
      this.onLimit?.()
    }
  }
}

/**
 * Decodes an audio file to mono 16 kHz PCM entirely in the browser. The
 * OfflineAudioContext does the resampling and the channel mixdown; nothing
 * here touches the network.
 */
export async function decodeAudioFile(file: File): Promise<Float32Array> {
  const buf = await file.arrayBuffer()
  const decodeCtx = new AudioContext()
  let decoded: AudioBuffer
  try {
    decoded = await decodeCtx.decodeAudioData(buf)
  } finally {
    await decodeCtx.close()
  }
  const offline = new OfflineAudioContext(
    1,
    Math.ceil(decoded.duration * 16000),
    16000
  )
  const source = offline.createBufferSource()
  source.buffer = decoded
  source.connect(offline.destination)
  source.start()
  const rendered = await offline.startRendering()
  return rendered.getChannelData(0)
}
